import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Upload, X, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface HubData {
  totals: {
    total: number;
    finished: number;
    inProgress: number;
    errors: number;
    notStarted: number;
  };
}

const DISMISS_KEY = "migrationHubBannerDismissed";

/**
 * Persistent dashboard reminder for the per-client migration queue.
 *
 * Hides when:
 *  - the agency has no clients yet (nothing to nudge about)
 *  - all clients are migrated (finished === total)
 *  - the broker dismissed it for this session
 *
 * Per-session dismissal via sessionStorage — reappears on next login.
 */
export function MigrationHubBanner() {
  const [, navigate] = useLocation();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.sessionStorage.getItem(DISMISS_KEY) === "1";
  });

  const hubQuery = useQuery<HubData>({
    queryKey: ["/api/migration/hub"],
    enabled: !dismissed,
  });

  const totals = hubQuery.data?.totals;
  if (dismissed) return null;
  if (!totals) return null;
  if (totals.total === 0) return null;
  if (totals.finished === totals.total) return null;

  const pct = Math.round((totals.finished / totals.total) * 100);

  const dismiss = () => {
    window.sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="flex items-center gap-4 rounded-lg border bg-card p-4">
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white"
        style={{
          background: "linear-gradient(135deg, #FED7AA, #F97316)",
        }}
      >
        <Upload className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-semibold">
          Migrate your client data — {totals.finished} of {totals.total}{" "}
          clients done
        </div>
        <div className="mb-2 text-xs text-muted-foreground">
          Import enrollment census for each client to bring their plans,
          employees and enrollments into Administr.
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="h-1.5 w-full max-w-[320px] overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="font-mono text-[11.5px] text-muted-foreground">
            {pct}%
          </span>
          {totals.inProgress > 0 && (
            <>
              <span className="text-[11.5px] text-muted-foreground/70">·</span>
              <Badge className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-50">
                {totals.inProgress} in progress
              </Badge>
            </>
          )}
          {totals.errors > 0 && (
            <Badge className="border-red-300 bg-red-50 text-red-700 hover:bg-red-50">
              <AlertTriangle className="mr-1 h-3 w-3" />
              {totals.errors} error{totals.errors === 1 ? "" : "s"}
            </Badge>
          )}
        </div>
      </div>
      <Button onClick={() => navigate("/migration/hub")} className="shrink-0">
        Continue migrations
        <ArrowRight className="ml-1 h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={dismiss}
        className="shrink-0"
        title="Dismiss for this session"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
